import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { CampaignStatus } from '@/nova/mock-data/types';
import { setBrandSwitchPhase } from './casino-instance';

/**
 * Runtime status changes for campaigns & promotions, keyed by id.
 *
 * Today these are the owner's pause/resume actions, layered over the mock pool
 * the pages still read for identity/metrics. The pool is the *seed*; this slice
 * is the *delta*. When the campaign/promotion endpoints land, the pause/resume
 * action creators become async thunks that POST to the API and the seed is
 * replaced by a fetched cache — the components and the `resolveCampaignStatus`
 * helper they call do not change (see `@/nova/utils/campaign-status`).
 *
 * Ids are globally unique across the acquisition campaign pool (s/r/p/e id
 * prefixes) and the operations promotion pool (sg/rn/ps/en prefixes), so a
 * single map is safe for both.
 */
export type CampaignStatusOverride = {
  status: CampaignStatus;
  /** When the change happened (epoch ms) — drives the "Paused on …" label. */
  at: number;
};

export type CampaignStatusState = {
  overrides: Record<string, CampaignStatusOverride>;
};

// Mock-stage persistence. Until the campaign/promotion endpoints land there is no
// server to hold the owner's activate/pause/resume, so a reload would otherwise
// revert every change to the seed pool (an activated campaign would reappear under
// Suggestions). Mirror the overrides into localStorage — the same slice-level
// pattern other slices use (see application.ts) — so the new state survives reloads
// and navigation. When the API lands this whole map is replaced by a fetched cache
// and these helpers go away.
const STORAGE_KEY = 'ows.campaignStatusOverrides';

const loadOverrides = (): Record<string, CampaignStatusOverride> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, CampaignStatusOverride>) : {};
  } catch {
    return {};
  }
};

const saveOverrides = (overrides: Record<string, CampaignStatusOverride>): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    /* storage unavailable / quota — persistence is best-effort */
  }
};

const initialState: CampaignStatusState = {
  overrides: loadOverrides(),
};

const campaignStatusSlice = createSlice({
  name: 'campaignStatus',
  initialState,
  reducers: {
    // `at` is supplied by the caller (the action hook) rather than read here, so
    // the reducer's *state* output stays deterministic / replay-safe; the only
    // side effect is mirroring the result to localStorage (mock-stage persistence,
    // see above — the same pattern application.ts uses for `language`).
    setCampaignStatus: (
      state,
      action: PayloadAction<{ id: string; status: CampaignStatus; at: number }>,
    ) => {
      const { id, status, at } = action.payload;
      state.overrides[id] = { status, at };
      saveOverrides(state.overrides);
    },
    /** Drop a runtime override, falling back to the seed/API status. */
    clearCampaignStatus: (state, action: PayloadAction<{ id: string }>) => {
      delete state.overrides[action.payload.id];
      saveOverrides(state.overrides);
    },
    /** Drop ALL runtime overrides — every campaign/promotion back to its seed status. */
    resetCampaignStatus: (state) => {
      state.overrides = {};
      saveOverrides(state.overrides);
    },
  },
  extraReducers: (builder) => {
    // Reset on a genuine brand switch. Each brand is its own casino, and the mock
    // pool/overrides aren't yet brand-scoped, so without this an owner who (say)
    // activated every suggestion would carry those emptied tabs onto the next
    // brand. The brand-switch phases ('out' → 'loading' → 'in') fire ONLY for a
    // real switch — an initial selection or same-brand re-select never sets a
    // phase (see `brandSwitchMiddleware`). We clear on 'in', the point the content
    // swaps to the new brand *behind the loader*, so the seed reappears unseen
    // (clearing on 'out' would flash the old brand's tabs repopulating mid-fade).
    builder.addCase(setBrandSwitchPhase, (state, action) => {
      if (action.payload === 'in' && Object.keys(state.overrides).length > 0) {
        state.overrides = {};
        saveOverrides(state.overrides);
      }
    });
  },
});

export const { setCampaignStatus, clearCampaignStatus, resetCampaignStatus } = campaignStatusSlice.actions;

export default campaignStatusSlice.reducer;
